import React from 'react';
import { Check, X, Clock } from 'lucide-react';

interface TimelineStepProps {
  title: string;
  date: string | null;
  isLast: boolean;
  status: string;
  /** Date is a forecast, not a completed event: keep the step styled as not yet done. */
  isExpected?: boolean;
}

export const TimelineStep: React.FC<TimelineStepProps> = ({
  title,
  date,
  isLast,
  status,
  isExpected = false,
}) => {
  const isNullDate = !date || isExpected;
  const isSuccess = status === 'success';
  const isFailed = status === 'failed';
  const isPending = !isSuccess && !isFailed;

  // Determine colors based on status
  const getStatusStyles = () => {
    if (isSuccess) {
      return {
        circleFill: isNullDate ? '#AEB4C6' : '#089B19',
        iconColor: isNullDate ? '#AEB4C6' : '#089B19',
        lineColor: isNullDate ? '#AEB4C6' : '#089B19',
        textColor: isNullDate ? 'text-light/40' : 'text-main',
        dateColor: isNullDate ? 'text-light/30' : 'text-light/50',
      };
    } else if (isFailed) {
      return {
        circleFill: '#AEB4C6',
        iconColor: '#AEB4C6',
        lineColor: '#AEB4C6',
        textColor: 'text-light/40',
        dateColor: 'text-light/30',
      };
    } else {
      // Orange for pending/other statuses
      return {
        circleFill: '#F59E0B',
        iconColor: '#F59E0B',
        lineColor: '#F59E0B',
        textColor: 'text-main',
        dateColor: 'text-light/50',
      };
    }
  };

  const styles = getStatusStyles();

  return (
    <div className={`relative flex items-start ${!isLast ? 'mb-8' : ''}`}>
      <div className='flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center'>
        <div className='relative'>
          <svg xmlns='http://www.w3.org/2000/svg' width='31' height='32' fill='none'>
            <circle
              cx='15.5'
              cy='16'
              r='15.5'
              fill={styles.circleFill}
              fillOpacity='0.1'
            />
          </svg>
          <div className='absolute inset-0 flex items-center justify-center'>
            {isFailed ? (
              <X className='w-4 h-4' style={{ color: styles.iconColor }} strokeWidth={3} />
            ) : isPending ? (
              <Clock className='w-4 h-4' style={{ color: styles.iconColor }} strokeWidth={2} />
            ) : (
              <Check
                className='w-4 h-4'
                style={{ color: styles.iconColor }}
                strokeWidth={3}
              />
            )}
          </div>
        </div>
      </div>
      {!isLast && (
        <div
          className='absolute w-[2px] h-[30px] top-7 left-3'
          style={{ backgroundColor: styles.lineColor }}
        ></div>
      )}
      <div className='ml-4 flex flex-col'>
        <p className={`font-medium text-sm ${styles.textColor}`}>{title}</p>
        {(isSuccess || isPending) && (
          <p className={`text-sm ${styles.dateColor}`}>{date ? date : 'Pending'}</p>
        )}
      </div>
    </div>
  );
};

